import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import { GoogleOAuthProvider } from "@react-oauth/google";
import "@kuika/kuika-cl-model/style/index.css";
import { QueryClientProvider } from "@tanstack/react-query";
import "animate.css/animate.min.css";
import "antd/dist/antd.min.css";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localeData from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import minMax from "dayjs/plugin/minMax";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";
import _ from "lodash";
import * as ReactDOM from "react-dom";
import { Provider } from "react-redux";
import rfdc from "rfdc";
import { captureHelpdeskDeepLink } from "./components/design-tools/helpdesk/helpdesk-deep-link";
import { installFormShake } from "./components/ui-kit/kuika-form-page/form-shake";
import { KuikaToastProvider } from "./components/ui-kit/kuika-toast/kuika-toast-provider";
import KuikaTooltipProvider from "./components/ui-kit/kuika-tooltip/kuika-tooltip-provider";
import "./auth/interceptors";
// Points the Monaco AMD loader at our self-hosted copy instead of the jsDelivr
// CDN. Must run before any editor mounts, hence the entry-level import.
import "./shared/utility/monaco-loader-config";
import { Router } from "./routes/router";
import { queryClient } from "./services/query-client";
import { followThemePreference } from "./shared/utility/designer-theme";
import { registerValidationDirtyInterceptor } from "./shared/utility/issues/validation-dirty-interceptor";
import configureStore from "./store/root-reducer";
import "./styles/database-diagram.scss";
import "./styles/device.scss";
import "./styles/form.scss";
import "./styles/general.scss";
import "./styles/layout.scss";
import "./styles/main.scss";
import "./styles/registration.scss";
import "./styles/styling.scss";

// Widens the Issues staleness signal to non-canvas mutations (workflows, jobs, API,
// datasources, data types). After the auth interceptors so refresh/retry stays inner.
registerValidationDirtyInterceptor();

// One capture listener for every DOM submit: an invalid antd/KModalFooter form shakes its
// invalid fields once and focuses the first. Forms that shake themselves (KuikaFormPage) opt out.
installFormShake();

// The pre-paint script already painted the saved theme; a "system" preference keeps following the OS.
followThemePreference();

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(isSameOrBefore);
dayjs.extend(relativeTime);
dayjs.extend(localeData);
dayjs.extend(minMax);
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

const store = configureStore();
const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

CommonHelperFunctions.ignoreKnownConsoleErrors();
CommonHelperFunctions.ignoreKnownConsoleWarns();

localStorage.removeItem("isWorkflowScreenStepWizardOpenByDefault");

// Before the router can send a signed-out user to the login page and drop the address.
captureHelpdeskDeepLink(window.location);

// Make life easier for people who use lodash
_.cloneDeep = rfdc();

ReactDOM.render(
  <GoogleOAuthProvider clientId="223861440043-irtfthtgph6qpq7a783d87dagb8jjhse.apps.googleusercontent.com">
    <Provider store={store}>
      <KuikaTooltipProvider>
        <KuikaToastProvider>
          <QueryClientProvider client={queryClient}>
            <Router />
          </QueryClientProvider>
        </KuikaToastProvider>
      </KuikaTooltipProvider>
    </Provider>
  </GoogleOAuthProvider>,
  rootElement
);
